import {
  Autocomplete,
  ListItem,
  ListItemText,
  TextField,
  TextFieldProps
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import useDebounce from '../../../hooks/useDebouncedFn/useDebouncedFn'
import { useLazySearchPartsTechSuppliersQuery } from '../../../redux/api/independentApi'
import { IdName } from '../../../sdk'
export interface ModifiedIdName extends IdName {
  inputValue?: string
}

const MIN_SEARCH_CHARS = 3

export const CreateSupplierAutocomplete = ({
  initialSupplier,
  supplier,
  setSupplier,
  ...props
}: {
  initialSupplier: ModifiedIdName | undefined
  supplier: ModifiedIdName | undefined
  setSupplier: React.Dispatch<React.SetStateAction<ModifiedIdName | undefined>>
} & TextFieldProps) => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const [triggerSearchSuppliers, { isLoading }] =
    useLazySearchPartsTechSuppliersQuery()

  const [input, setInput] = useState(initialSupplier?.name || '')
  const debouncedInput = useDebounce<string>(input)
  const [open, setOpen] = useState(false)
  const [supplierOptions, setSupplierOptions] = useState<ModifiedIdName[]>(
    initialSupplier ? [initialSupplier] : []
  )
  const initialValueUntouched =
    !!initialSupplier &&
    input === initialSupplier.name &&
    JSON.stringify(supplier) === JSON.stringify(initialSupplier)

  const getResultsFn = useCallback(
    async (searchTerm: string) => {
      const response = await triggerSearchSuppliers({
        authorization: accessToken,
        keyword: searchTerm,
        shop: shopId
      }).unwrap()
      setSupplierOptions([
        ...response,
        {
          id: undefined,
          name: `Add "${searchTerm}"`,
          inputValue: searchTerm
        }
      ])
    },
    [triggerSearchSuppliers, accessToken, shopId]
  )

  useEffect(() => {
    if (debouncedInput?.length >= MIN_SEARCH_CHARS && !initialValueUntouched) {
      getResultsFn(debouncedInput)
    } else {
      setSupplierOptions([])
    }
  }, [debouncedInput, getResultsFn, initialValueUntouched])

  return (
    <Autocomplete
      id="supplierSearch"
      open={open}
      getOptionLabel={(option) => option.inputValue || option.name!}
      options={supplierOptions}
      autoComplete
      value={supplier}
      loading={isLoading}
      onChange={(
        event: React.SyntheticEvent,
        newValue: ModifiedIdName | null
      ) => {
        if (!newValue) {
          setSupplier(undefined)
        } else {
          setSupplier(newValue)
        }
        setOpen(false)
      }}
      onInputChange={(event, newInputValue) => {
        setInput(newInputValue)
        if (!newInputValue || initialValueUntouched) {
          setOpen(false)
        } else {
          setOpen(true)
        }
      }}
      renderInput={(params) => (
        <TextField {...params} label="Select a Supplier" fullWidth {...props} />
      )}
      renderOption={(props, option) => {
        return (
          <ListItem sx={{ padding: 0 }} {...props} key={option.name}>
            <ListItemText primary={option.name} />
          </ListItem>
        )
      }}
      noOptionsText={
        input && input.length < MIN_SEARCH_CHARS
          ? `Please enter at least ${MIN_SEARCH_CHARS} characters to search`
          : 'No options found'
      }
      fullWidth
    />
  )
}
